import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { auth, db } from '../lib/firebase';
import { useUserProfile } from '../hooks/useUserProfile';
import { getDefaultLanguage, isLanguageSupported } from '../utils/languages';
import { getMessages, type MessageKey } from '../translations';

type LocaleContextType = {
  locale: string;
  messages: Record<MessageKey, string>;
  t: (key: MessageKey) => string;
  setLocale: (code: string) => Promise<void>;
};

const LocaleContext = createContext<LocaleContextType | undefined>(undefined);

function readStoredLanguage(): string | null {
  try {
    const stored = localStorage.getItem('language');
    return stored && isLanguageSupported(stored) ? stored : null;
  } catch {
    return null;
  }
}

export default function DynamicLocaleProvider({ children }: { children: ReactNode }) {
  const { language, loading, user } = useUserProfile();
  const [localeOverride, setLocaleOverride] = useState<string | null>(() => readStoredLanguage());

  const effectiveLocale = useMemo(() => {
    if (localeOverride && isLanguageSupported(localeOverride)) {
      return localeOverride;
    }

    if (!loading && user && language && isLanguageSupported(language)) {
      return language;
    }

    return readStoredLanguage() ?? getDefaultLanguage();
  }, [language, loading, localeOverride, user]);

  const messages = useMemo(() => getMessages(effectiveLocale), [effectiveLocale]);

  useEffect(() => {
    document.documentElement.lang = effectiveLocale;
    auth.languageCode = effectiveLocale;
  }, [effectiveLocale]);

  const setLocale = useCallback(
    async (code: string) => {
      if (!isLanguageSupported(code)) return;

      setLocaleOverride(code);

      try {
        localStorage.setItem('language', code);
      } catch {
        // localStorage may be unavailable
      }

      document.documentElement.lang = code;
      auth.languageCode = code;

      if (user?.uid) {
        try {
          await updateDoc(doc(db, 'users', user.uid), { language: code });
        } catch (error) {
          console.error('Failed to save language preference:', error);
        }
      }
    },
    [user]
  );

  const value = useMemo<LocaleContextType>(
    () => ({
      locale: effectiveLocale,
      messages,
      t: (key: MessageKey) => messages[key] ?? key,
      setLocale,
    }),
    [effectiveLocale, messages, setLocale]
  );

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export const useLocale = (): LocaleContextType => {
  const ctx = useContext(LocaleContext);
  if (!ctx) throw new Error('useLocale must be used inside DynamicLocaleProvider');
  return ctx;
};
